// First-party analytics context: anonymous browser/device profile, session, first-touch
// attribution and a coarse device class.
//
// PRIVACY: nothing here identifies a person. No names, emails, phone numbers, IP addresses,
// precise location, form contents or search terms are read or sent. `hhl_vid` is a random id for
// an anonymous browser/device profile — a new browser, a new device or cleared storage counts as a
// separate visitor. Only the referrer's HOST is captured, never its full URL, so inbound search
// queries never leave the referring site.
import { useEffect } from "react";

const VISITOR_KEY = "hhl_vid";
const VISITOR_META_KEY = "hhl_vmeta";
const SESSION_KEY = "hhl_sid";
const SESSION_IDLE_MS = 30 * 60 * 1000;

const readJson = (key) => {
  try {
    return JSON.parse(window.localStorage.getItem(key) || "null");
  } catch {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode or storage disabled: analytics degrades, the site does not */
  }
};

const randomId = () => {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID().replace(/-/g, "");
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 14)}`;
  }
};

// Coarse classification only — no canvas, font or hardware fingerprinting.
const deviceClass = () => {
  const ua = window.navigator?.userAgent || "";
  if (!ua) return "unknown";
  if (/\b(iPad|Tablet|PlayBook|Silk)\b/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua)))
    return "tablet";
  if (window.navigator.userAgentData?.mobile) return "mobile";
  if (/Mobi|iPhone|iPod|Android.*Mobile|Windows Phone|IEMobile/i.test(ua)) return "mobile";
  return "desktop";
};

const SOCIAL_HOSTS = {
  "pinterest.com": "pinterest",
  "pin.it": "pinterest",
  "instagram.com": "instagram",
  "youtube.com": "youtube",
  "youtu.be": "youtube",
  "facebook.com": "facebook",
  "fb.com": "facebook",
  "fb.me": "facebook",
  "tiktok.com": "tiktok",
  "reddit.com": "reddit",
  "twitter.com": "x",
  "x.com": "x",
  "t.co": "x",
  "linkedin.com": "linkedin",
  "threads.net": "threads",
  "threads.com": "threads",
  "snapchat.com": "snapchat",
  "nextdoor.com": "nextdoor",
};

const SEARCH_ENGINES = [
  "google", "bing", "duckduckgo", "yahoo", "ecosia", "brave", "startpage", "qwant", "baidu",
  "yandex", "ask", "aol", "search",
];

// AI assistants and answer engines. Kept separate from search engines so Perplexity and friends are
// never reported as ordinary organic search, and each assistant keeps its own identity.
const AI_HOSTS = {
  "chatgpt.com": "ChatGPT",
  "chat.openai.com": "ChatGPT",
  "openai.com": "ChatGPT",
  "claude.ai": "Claude",
  "anthropic.com": "Claude",
  "perplexity.ai": "Perplexity",
  "copilot.microsoft.com": "Microsoft Copilot",
  "bing.com/chat": "Microsoft Copilot",
  "gemini.google.com": "Gemini",
  "bard.google.com": "Gemini",
  "you.com": "You.com",
};

const aiAssistant = (value) => {
  const v = (value || "").toLowerCase().replace(/^www\./, "");
  if (!v) return null;
  if (AI_HOSTS[v]) return AI_HOSTS[v];
  // Bare UTM forms such as utm_source=chatgpt or utm_source=perplexity.
  const bare = { chatgpt: "ChatGPT", openai: "ChatGPT", claude: "Claude", anthropic: "Claude",
    perplexity: "Perplexity", copilot: "Microsoft Copilot", gemini: "Gemini", bard: "Gemini",
    "you.com": "You.com" };
  return bare[v] || AI_HOSTS[`${v}.com`] || AI_HOSTS[`${v}.ai`] || null;
};

// Returns the search engine name when a host belongs to one, e.g. search.yahoo.co.uk -> yahoo.
const searchEngine = (host) => {
  const labels = (host || "").split(".");
  const hit = SEARCH_ENGINES.find((name) => labels.includes(name) && name !== "search");
  return hit || (labels.includes("search") ? labels[labels.indexOf("search") + 1] || null : null);
};

const CLICK_ID_SOURCES = [
  ["gclid", "google", "paid_search"],
  ["gbraid", "google", "paid_search"],
  ["wbraid", "google", "paid_search"],
  ["msclkid", "bing", "paid_search"],
  ["fbclid", "facebook", "social"],
  ["igshid", "instagram", "social"],
  ["ttclid", "tiktok", "social"],
  ["epik", "pinterest", "social"],
  ["li_fat_id", "linkedin", "social"],
];

// Strips "www." and any leading "l.", "lm.", "m.", "out." style redirector prefixes so
// l.instagram.com and instagram.com are one source.
const rootHost = (host) => {
  const h = (host || "").toLowerCase().replace(/^www\./, "");
  const parts = h.split(".");
  if (parts.length > 2) {
    const tail = parts.slice(-2).join(".");
    if (SOCIAL_HOSTS[tail]) return tail;
    const tail3 = parts.slice(-3).join(".");
    if (SOCIAL_HOSTS[tail3]) return tail3;
  }
  return h;
};

export const landingPageType = (path) => {
  if (path === "/" || path === "") return "home";
  if (path === "/links") return "links";
  if (path.startsWith("/hub/")) return "hub";
  if (path.startsWith("/guides/")) return "guide";
  if (path.startsWith("/tools/")) return "tool";
  if (path === "/tools") return "tools_index";
  if (path.startsWith("/admin")) return "admin";
  return "page";
};

// First-touch attribution for a session, resolved once when the session starts.
const resolveAttribution = () => {
  const params = new URLSearchParams(window.location.search);
  const get = (k) => (params.get(k) || "").slice(0, 120).trim();
  const utmSource = get("utm_source");
  const utmMedium = get("utm_medium").toLowerCase();
  const campaign = get("utm_campaign");
  const campaignContent = get("utm_content");
  const campaignTerm = get("utm_term");

  let refHost = "";
  try {
    if (document.referrer) {
      const url = new URL(document.referrer);
      if (url.host && url.host !== window.location.host) refHost = rootHost(url.host);
    }
  } catch {
    refHost = "";
  }

  let channel = "";
  let source = "";

  if (utmSource || utmMedium) {
    source = (utmSource || refHost || "unknown").toLowerCase();
    const ai = aiAssistant(source);
    if (ai) {
      return {
        channel: "ai_assistant",
        source: ai,
        medium: utmMedium.slice(0, 120) || null,
        campaign: campaign || null,
        campaign_content: campaignContent || null,
        campaign_term: campaignTerm || null,
        referrer_host: refHost || null,
        landing_page: window.location.pathname.slice(0, 200),
        landing_page_type: landingPageType(window.location.pathname),
      };
    }
    const social = SOCIAL_HOSTS[rootHost(source)] || SOCIAL_HOSTS[`${source}.com`] || source;
    const isSearchSource = Boolean(searchEngine(source) || SEARCH_ENGINES.includes(source));
    if (/^(cpc|ppc|paid|paidsearch|paid_search|sem|cpa|cpm)$/.test(utmMedium))
      channel = isSearchSource ? "paid_search" : "paid_social";
    else if (/^(email|newsletter|e-mail)$/.test(utmMedium)) channel = "email";
    else if (/^(social|social_paid|paid_social|social-paid)$/.test(utmMedium)) {
      channel = utmMedium.includes("paid") ? "paid_social" : "social";
      source = social;
    } else if (/^(organic|search)$/.test(utmMedium)) channel = "organic_search";
    else if (/^(referral|affiliate|partner)$/.test(utmMedium)) channel = "referral";
    else if (SOCIAL_HOSTS[rootHost(source)]) {
      channel = "social";
      source = SOCIAL_HOSTS[rootHost(source)];
    } else channel = "campaign";
  }

  if (!channel) {
    const clickId = CLICK_ID_SOURCES.find(([param]) => params.get(param));
    if (clickId) {
      source = clickId[1];
      channel = clickId[2];
    }
  }

  if (!channel && refHost) {
    const ai = aiAssistant(refHost);
    if (ai) {
      channel = "ai_assistant";
      source = ai;
    } else if (SOCIAL_HOSTS[refHost]) {
      channel = "social";
      source = SOCIAL_HOSTS[refHost];
    } else if (searchEngine(refHost)) {
      channel = "organic_search";
      source = searchEngine(refHost);
    } else {
      channel = "referral";
      source = refHost;
    }
  }

  if (!channel) {
    channel = "direct";
    source = "direct";
  }

  return {
    channel,
    source: (source || "").slice(0, 120),
    medium: utmMedium.slice(0, 120) || null,
    campaign: campaign || null,
    campaign_content: campaignContent || null,
    campaign_term: campaignTerm || null,
    referrer_host: refHost || null,
    landing_page: window.location.pathname.slice(0, 200),
    landing_page_type: landingPageType(window.location.pathname),
  };
};

// Returns the live session, starting a new one after SESSION_IDLE_MS of inactivity.
const currentSession = () => {
  const now = Date.now();
  let visitorId = null;
  try {
    visitorId = window.localStorage.getItem(VISITOR_KEY);
  } catch {
    visitorId = null;
  }
  const meta = readJson(VISITOR_META_KEY) || {};
  let isNewVisitor = false;

  if (!visitorId) {
    visitorId = randomId();
    isNewVisitor = true;
    try {
      window.localStorage.setItem(VISITOR_KEY, visitorId);
    } catch {
      /* ignore */
    }
  }

  let session = readJson(SESSION_KEY);
  const expired = !session || !session.sid || now - (session.last_activity || 0) > SESSION_IDLE_MS;

  if (expired) {
    session = {
      sid: randomId(),
      started_at: now,
      last_activity: now,
      visitor_type: isNewVisitor || !meta.first_seen ? "new" : "returning",
      attribution: resolveAttribution(),
    };
    const sessionCount = (meta.sessions || 0) + 1;
    writeJson(VISITOR_META_KEY, {
      first_seen: meta.first_seen || new Date(now).toISOString(),
      last_seen: new Date(now).toISOString(),
      sessions: sessionCount,
    });
  } else {
    session.last_activity = now;
    writeJson(VISITOR_META_KEY, {
      first_seen: meta.first_seen || new Date(now).toISOString(),
      last_seen: new Date(now).toISOString(),
      sessions: meta.sessions || 1,
    });
  }
  writeJson(SESSION_KEY, session);
  return { visitorId, session };
};

// Flat analytics context merged into every event.
export function analyticsContext() {
  try {
    const { visitorId, session } = currentSession();
    const path = window.location.pathname;
    const hubMatch = path.match(/^\/hub\/([a-z0-9-]+)/i);
    return {
      visitor_id: visitorId,
      session_id: session.sid,
      visitor_type: session.visitor_type,
      device: deviceClass(),
      hub: hubMatch ? hubMatch[1] : null,
      page_type: landingPageType(path),
      ...session.attribution,
    };
  } catch {
    return {};
  }
}

// A product/partner view means the card was at least half visible for a full second — not merely
// present in the DOM. Fires at most once per card per mount.
export function useCardView(ref, payload, enabled = true) {
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled || typeof window.IntersectionObserver !== "function") return undefined;
    let timer = null;
    let sent = false;
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !sent && !timer) {
            timer = window.setTimeout(() => {
              sent = true;
              timer = null;
              observer.disconnect();
              // Imported lazily to avoid a circular import with lib/api.
              import("@/lib/api").then((m) => m.track(payload)).catch(() => {});
            }, 1000);
          } else if (!entry.isIntersecting && timer) {
            window.clearTimeout(timer);
            timer = null;
          }
        });
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => {
      if (timer) window.clearTimeout(timer);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, enabled, JSON.stringify(payload)]);
}
